import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { normalizeLaunchModuleKey } from "@/lib/launchPath";
import type { AnyRow } from "@/lib/rowTypes";

export interface LaunchPathItem {
  id: string;
  position: number;
  itemType: "lesson" | "simulator";
  refId: string;
  title: string;
  subtitle: string | null;
  completed: boolean;
  /**
   * True when the previous launch path item is not yet complete. The very
   * first item is never locked. Use this to gate navigation and render a
   * lock affordance in the Launch Path UI.
   */
  locked: boolean;
}

export interface LaunchPathState {
  items: LaunchPathItem[];
  completedCount: number;
  totalCount: number;
  nextItem: LaunchPathItem | null;
  done: boolean;
  loading: boolean;
  markComplete: (launchItemId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * useLaunchPath
 * Reads the admin-curated launch_path_items and overlays per-learner
 * completion from learner_launch_path. Falls back to an empty list (no
 * items active) so callers can hide the UI seamlessly.
 */
export function useLaunchPath(): LaunchPathState {
  const { user } = useAuth();
  const { isAdmin } = useAdminRole();
  const [items, setItems] = useState<LaunchPathItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const client = supabase;
      const { data: rows, error: rowsErr } = await client
        .from("launch_path_items")
        .select("id, position, item_type, ref_id, title, subtitle")
        .eq("is_active", true)
        .order("position", { ascending: true });
      if (rowsErr) throw rowsErr;

      const lessonRefs = (rows || [])
        .filter((r: AnyRow) => r.item_type === "lesson")
        .map((r: AnyRow) => r.ref_id);
      const simulatorRefs = (rows || [])
        .filter((r: AnyRow) => r.item_type === "simulator")
        .map((r: AnyRow) => normalizeLaunchModuleKey(r.ref_id))
        .filter(Boolean);

      const [lessonProgressRes, simProgressRes, ownLaunchRes] = await Promise.all([
        user && lessonRefs.length
          ? client
              .from("user_progress")
              .select("lesson_id")
              .eq("user_id", user.id)
              .eq("completed", true)
              .in("lesson_id", lessonRefs)
          : Promise.resolve({ data: [] as { lesson_id: string }[] }),
        user && simulatorRefs.length
          ? client
              .from("module_completions")
              .select("module_key")
              .eq("user_id", user.id)
              .eq("passed", true)
              .in("module_key", simulatorRefs)
          : Promise.resolve({ data: [] as { module_key: string }[] }),
        user
          ? client
              .from("learner_launch_path")
              .select("launch_item_id")
              .eq("user_id", user.id)
          : Promise.resolve({ data: [] as { launch_item_id: string }[] }),
      ]);

      const completedLessons = new Set<string>(
        (lessonProgressRes.data || []).map((p: AnyRow) => p.lesson_id),
      );
      const completedSims = new Set<string>(
        (simProgressRes.data || []).map((p: AnyRow) => p.module_key),
      );
      const completedLaunch = new Set<string>(
        (ownLaunchRes.data || []).map((p: AnyRow) => p.launch_item_id),
      );

      const base = (rows || []).map((r: AnyRow) => {
        const moduleKey = r.item_type === "simulator" ? normalizeLaunchModuleKey(r.ref_id) : null;
        const derivedDone =
          r.item_type === "lesson"
            ? completedLessons.has(r.ref_id)
            : !!moduleKey && completedSims.has(moduleKey);
        return {
          id: r.id as string,
          position: r.position as number,
          itemType: r.item_type as "lesson" | "simulator",
          refId: r.ref_id as string,
          title: r.title as string,
          subtitle: (r.subtitle as string | null) ?? null,
          completed: derivedDone || completedLaunch.has(r.id),
        };
      });
      // Enforce strict sequential unlock: an item is locked unless every
      // earlier item is complete. The first item is always unlocked.
      let allPriorComplete = true;
      const withLocks: LaunchPathItem[] = base.map((it, idx) => {
        // Admins bypass sequential gating so they can preview any module.
        const locked = isAdmin ? false : idx === 0 ? false : !allPriorComplete;
        if (!it.completed) allPriorComplete = false;
        return { ...it, locked };
      });
      setItems(withLocks);
    } catch (err) {
      console.error("useLaunchPath load error:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const markComplete = useCallback(
    async (launchItemId: string) => {
      if (!user) return;
      try {
        const client = supabase;
        await client
          .from("learner_launch_path")
          .upsert(
            { user_id: user.id, launch_item_id: launchItemId },
            { onConflict: "user_id,launch_item_id", ignoreDuplicates: true },
          );
        setItems((prev) =>
          prev.map((it) => (it.id === launchItemId ? { ...it, completed: true } : it)),
        );
      } catch (err) {
        console.error("useLaunchPath markComplete error:", err);
      }
    },
    [user],
  );

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;
  const nextItem = items.find((i) => !i.completed) ?? null;
  const done = totalCount > 0 && completedCount === totalCount;

  return {
    items,
    completedCount,
    totalCount,
    nextItem,
    done,
    loading,
    markComplete,
    refetch: load,
  };
}